import React from 'react';
import Button from './ui/Button';

const ClassList = ({
  classes = [],
  classType,
  onJoinClass,
  onLeaveClass,
  onSetAvailability
}) => {
  // Helper function to convert time string to minutes for sorting
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let totalMinutes = (hours % 12) * 60 + minutes;
    if (period === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (period === 'AM' && hours === 12) {
      totalMinutes = minutes; // 12 AM is 00:00
    }
    
    return totalMinutes;
  };

  // Group classes by day of the week
  const groupClassesByDay = () => {
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const groupedClasses = {};
    
    // Initialize all days
    dayOrder.forEach(day => {
      groupedClasses[day] = [];
    });
    
    // Group classes by day
    classes.forEach(classData => {
      if (classData.day && groupedClasses[classData.day]) {
        groupedClasses[classData.day].push(classData);
      }
    });
    
    // Sort classes within each day by time (chronological order)
    dayOrder.forEach(day => {
      groupedClasses[day].sort((a, b) => {
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      });
    });
    
    // Return all days (including empty ones for calendar layout)
    return dayOrder.reduce((result, day) => {
      result[day] = groupedClasses[day];
      return result;
    }, {});
  };

  const groupedClasses = groupClassesByDay();
  const totalClassCount = classes.length;

  // Helper function to get short day names
  const getShortDay = (day) => {
    const shortDays = {
      'Monday': 'Mon',
      'Tuesday': 'Tue', 
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    return shortDays[day] || day;
  };

  // Helper function to render individual class card
  const renderClassCard = (classData) => {
    const isJoinable = classData.status === 'active' && classData.spotsLeft > 0 && !classData.isEnrolled;
    const isWaitlistable = classData.status === 'full' && !classData.isEnrolled;
    const isEnrolled = classData.isEnrolled;

    return (
      <div
        key={classData.$id}
        className="bg-gray-700 bg-opacity-50 rounded-lg p-3 border border-gray-600 mb-3 transition-all duration-200 hover:bg-gray-600 hover:bg-opacity-50"
      >
        {/* Time */}
        <div className="text-white font-medium text-base mb-2 text-center">
          {classData.time}
        </div>
        
        {/* Class Type Name */}
        <div className="text-center mb-2">
          <div className="text-purple-300 text-sm font-medium">
            {classData.classTypeName || 'Class'}
          </div>
        </div>
        
        {/* Enrolled badge if user is enrolled */}
        {isEnrolled && (
          <div className="mb-2 text-center">
            <span className="text-xs bg-green-500 bg-opacity-20 text-green-400 px-2 py-1 rounded-full font-medium">
              Enrolled
            </span>
          </div>
        )}
        
        {/* Progress bar */}
        {!isEnrolled && (
          <div className="mb-3">
            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-1">
              <div
                className={`h-1.5 rounded-full ${
                  classData.status === 'cancelled' ? 'bg-red-500' :
                  classData.status === 'completed' ? 'bg-gray-500' :
                  'bg-[#7e55f6]'
                }`}
                style={{ width: `${classData.totalSpots > 0 ? ((classData.totalSpots - classData.spotsLeft) / classData.totalSpots) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="text-gray-400 text-xs text-center">
              {classData.spotsLeft > 0 && classData.status !== 'cancelled' && classData.status !== 'completed'
                ? `${classData.spotsLeft} spots`
                : (classData.status === 'full' || classData.spotsLeft === 0) && classData.status !== 'cancelled' && classData.status !== 'completed'
                  ? 'Full'
                  : classData.status === 'cancelled' ? 'Cancelled' :
                    classData.status === 'completed' ? 'Completed' : ''
              }
            </div>
          </div>
        )}

        {/* Enrolled message */}
        {isEnrolled && (
          <div className="mb-3 text-center">
            <div className="text-green-400 text-xs font-medium">
              See you there!
            </div>
          </div>
        )}
        
        {/* Action button */}
        <div className="flex justify-center">
          {isJoinable && (
            <Button
              variant="primary"
              size="small"
              onClick={() => onJoinClass(classData)}
              className="text-xs px-3 py-1.5"
            >
              Join
            </Button>
          )}
          {isWaitlistable && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => onJoinClass(classData)}
              className="text-xs px-3 py-1.5"
            >
              Waitlist
            </Button>
          )}
          {isEnrolled && (
            <Button
              variant="outline"
              size="small"
              onClick={() => onLeaveClass(classData)}
              className="border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10 hover:border-red-400 text-xs px-3 py-1.5"
            >
              Leave
            </Button>
          )}
          {!isJoinable && !isWaitlistable && !isEnrolled && (
            <span className="text-xs text-gray-400 px-2 py-1">
              {classData.status === 'cancelled' ? 'Cancelled' :
               classData.status === 'completed' ? 'Completed' :
               classData.status === 'pending' ? 'Coming Soon' :
               'Not Available'}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {totalClassCount > 0 ? (
        <>
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
            <h3 className="font-medium text-lg mb-2">
              {totalClassCount} Available Class{totalClassCount !== 1 ? 'es' : ''}
              {classType !== 'all' && ` in ${classType.charAt(0).toUpperCase() + classType.slice(1).replace('_', ' ')}`}
            </h3>
            <p className="mb-6 text-gray-300">
              Select a class time that works for you. You'll be able to confirm your information before joining.
            </p>
            
            {/* Calendar layout - Days as columns */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-4">
              {Object.entries(groupedClasses).map(([day, dayClasses]) => (
                <div key={day} className="min-h-[200px]">
                  {/* Day header */}
                  <div className="text-center mb-4 pb-2 border-b border-gray-600">
                    <h4 className="text-base font-medium text-white">
                      <span className="hidden sm:inline">{day}</span>
                      <span className="sm:hidden">{getShortDay(day)}</span>
                    </h4>
                  </div>
                  
                  {/* Classes for this day (now sorted chronologically) */}
                  <div className="space-y-3">
                    {dayClasses.length > 0 ? (
                      dayClasses.map(renderClassCard)
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-gray-500 text-sm">
                          No classes
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6">
          <p className="text-gray-300 mb-2">
            There are no available classes at the moment
            {classType !== 'all' && ` in ${classType.charAt(0).toUpperCase() + classType.slice(1).replace('_', ' ')}`}.
          </p>
          <p className="text-gray-400">
            You can set your availability below and we'll notify you when a new class forms.
          </p>
        </div>
      )}

      {/* None of these times work section */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 mt-8">
        <div className="mb-6">
          <h3 className="text-xl font-medium text-center">
            None of these class times work for you?
          </h3>
        </div>
        
        <p className="text-gray-300 text-center mb-6">
          Tell us when you're available and we'll notify you when a class forms at your preferred time.
        </p>
        
        <div className="flex justify-center">
          <Button
            variant="primary"
            onClick={() => onSetAvailability(classType)}
            className="bg-purple-600 hover:bg-purple-700 px-6"
          >
            Set My Availability
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClassList;